import { type WorkspaceAgentBeforeAfterRow } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/tables/WorkspaceAgentBeforeAfterTable';
import { type ChangePreviewPill } from '~/components/v2/components/workspace/workspace-agent-panel/schemas/pillMetadata';

type AgentChangePreviewValue = ChangePreviewPill['rows'][number]['changes'][number]['before'];

const getChangePreviewPillCountNoun = (rows: ChangePreviewPill['rows']): string => {
  const firstProperty = rows[0]?.changes[0]?.property;

  if (firstProperty === 'filename') {
    return 'files';
  }

  return 'items';
};

export const formatWorkspaceAgentChangePreviewValue = (value: AgentChangePreviewValue): string => {
  if (value === null) {
    return '—';
  }

  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  if (Array.isArray(value)) {
    return value.map(formatWorkspaceAgentChangePreviewValue).join(', ');
  }

  return JSON.stringify(value);
};

/** Caption beneath the table when the pill carries a sample rather than the full change set. */
export const formatWorkspaceAgentChangePreviewPillCaption = ({
  count,
  rows,
  truncated,
}: Pick<ChangePreviewPill, 'count' | 'rows' | 'truncated'>): string | undefined => {
  if (!truncated && rows.length >= count) {
    return undefined;
  }

  const noun = getChangePreviewPillCountNoun(rows);

  return `Showing ${rows.length} of ${count} ${noun}`;
};

export const mapWorkspaceAgentChangePreviewPillRowsToBeforeAfterTable = (
  rows: ChangePreviewPill['rows'],
): WorkspaceAgentBeforeAfterRow[] =>
  rows.flatMap((row) =>
    row.changes.map((change) => {
      const before = formatWorkspaceAgentChangePreviewValue(change.before);
      const after = formatWorkspaceAgentChangePreviewValue(change.after);

      if (row.changes.length === 1) {
        return { after, before };
      }

      const label = change.label ?? change.property;

      return { after: `${label}: ${after}`, before: `${label}: ${before}` };
    }),
  );
